import React, { useContext } from 'react';
import { SubsectionContext } from 'components/Section/shared/SubsectionConnector';
import { DataAttributeType } from 'helpers/attributes/types';
import { isDataAttributeObject } from 'helpers/attributes/attributes';

interface AnchorProps {
  id: string
}

function isStringArray(array: any): array is string[] {
  return typeof array[0] === 'string';
}

// Some characers need to be escaped to query, so it is easier to replace it
// to provide an easier query.
export const getFriendlySelector = (id: string) => id.replace(/[.[\]]/g, '-').toLowerCase();

/**
 * Anchor provides a location that can be navigated. However, when the id is
 * undefined or has undefined, it won't render anything at all.
 */
export const Anchor: React.FC<AnchorProps> = ({ id }) => {
  if (id.includes('undefined') || !id) { return null; }

  return <span id={getFriendlySelector(id)} className="anchorComponent" aria-hidden="true" />;
};

interface AttributeAnchorsProps {
  /**
   * Attribute is a required field, however, components referencing this may
   * have "attribute" as "string" | "undefined". For the purpose of the
   * validation migration, this is okay, however, once the validation migration
   * is done, attribute should be required across the board.
   */
  attribute: DataAttributeType

  prefix?: string
}

/**
 * AttributeAnchors is a instance of Anchor. AttributeAnchors will generate
 * anchor(s) associated to the attribute(s), subsection, and prefix. This will
 * allow other implementations to be able to focus these anchors as needed.
 */
export const AttributeAnchors: React.FC<AttributeAnchorsProps> = ({
  attribute,
  prefix = 'input',
}) => {
  const context = useContext(SubsectionContext);
  const prefixId = `${prefix}--${context.subsectionKey?.replace(/_/g, '.')}`;

  // handles Array
  if (Array.isArray(attribute)) {
    if (attribute.length > 0) {
      // handles string[]
      if (isStringArray(attribute)) {
        return (
          <>
            {attribute.map((attr, index) => (
              <Anchor id={`${prefixId}.${attr}`} key={`${prefixId}.${attr}.${index}`} />
            ))}
          </>
        );
      }

      // handles DataAttributeObject[]
      return (
        <>
          {attribute.map((obj, index) => (
            <Anchor id={`${prefixId}.${obj.value}`} key={`${prefixId}.${obj.value}.${index}`} />
          ))}
        </>
      );
    }
    // if this is an array and is empty, this should return nothing
    return null;
  }

  // handles string
  if (typeof attribute === 'string') {
    return <Anchor id={`${prefixId}.${attribute}`} />;
  }

  // handles DataAttributeObject
  if (typeof attribute === 'object' && isDataAttributeObject(attribute)) {
    return <Anchor id={`${prefixId}.${attribute.value}`} />;
  }

  return null;
};
